/**
 * Znak marki „for apps".
 *
 * Geometria jest przepisana z karty logo co do proporcji (siatka 100×100):
 * kwadrat o promieniu 28, cztery znaczniki po 22 jednostki w osiach 28 i 71.6.
 * Karta zabrania zmieniać proporcje sygnetu i odstęp do wordmarku, więc
 * wszystko skaluje się jednym parametrem `size` — nie ma tu osobnych pokręteł.
 *
 * Trzy warianty, bo karta zabrania stawiać czerwoną wersję na czerwonym albo
 * ciemnym tle:
 *   `primary` — czerwony kwadrat na jasnym,
 *   `reverse` — biały kwadrat na ciemnym (motyw ciemny panelu),
 *   `mono`    — kwadrat w kolorze Ink, gdy kolor jest niedostępny (druk, PDF).
 * Znaczniki są zawsze wycięciem w kolorze tła kafla, a czwarty ma 55% krycia.
 */

export type Wariant = "primary" | "reverse" | "mono";

const PALETA: Record<Wariant, { kafel: string; znacznik: string }> = {
  primary: { kafel: "#A10000", znacznik: "#FFFFFF" },
  reverse: { kafel: "#FFFFFF", znacznik: "#0E0E10" },
  mono: { kafel: "#0E0E10", znacznik: "#FFFFFF" },
};

export function Mark({
  size = 32,
  wariant = "primary",
  className,
}: {
  size?: number;
  wariant?: Wariant;
  className?: string;
}) {
  const { kafel, znacznik } = PALETA[wariant];
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label="for apps"
      focusable="false"
    >
      <rect width="100" height="100" rx="28" fill={kafel} />
      <circle cx="28.2" cy="28.2" r="11" fill={znacznik} />
      <rect x="60.8" y="17.4" width="21.6" height="21.6" rx="5" fill={znacznik} />
      <path d="M28 59.8 38.9 80.2H17.1Z" fill={znacznik} />
      {/* Czwarty znacznik jest przygaszony — to on odróżnia sygnet od kostki
          do gry i daje mu rytm. Krycie 55% zmierzone z karty. */}
      <circle cx="71.6" cy="71.6" r="10.8" fill={znacznik} fillOpacity="0.55" />
    </svg>
  );
}

/**
 * Pełny lockup: sygnet i wordmark.
 *
 * Wordmark jest tekstem, nie ścieżkami — dzięki temu skaluje się z resztą
 * interfejsu, da się go zaznaczyć i przeczytać czytnikiem ekranu. Nazwa
 * zawsze małymi literami; karta zabrania wersalików i Title Case, więc
 * `text-transform: lowercase` siedzi w CSS jako zabezpieczenie.
 */
export function Logo({
  size = 32,
  wariant = "primary",
  className,
}: {
  size?: number;
  wariant?: Wariant;
  className?: string;
}) {
  return (
    <span className={`logo${className ? ` ${className}` : ""}`} data-wariant={wariant}>
      <Mark size={size} wariant={wariant} />
      <span className="logo__slowo" style={{ fontSize: size * 0.72 }}>
        for apps
      </span>
    </span>
  );
}
